import React, { useEffect } from "react";
import {
  Shield,
  Mic,
  FileText,
  PieChart,
  Lock,
  MessageSquare,
} from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { VideoEmbed } from "../ui/VideoEmbed/VideoEmbed";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";
import LloydsVideo from "../../../assets/videos/lloyds.mp4";

import "./Lloyds.scss";

const Lloyds = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Lloyds — Money Talks"
        subtitle="Voice-enabled banking explored through responsible design"
        category="ENGINEERING"
        role="Lead Engineer"
        year="2017"
        platform="Amazon Echo & Alexa Voice Assistant"
        technologies={["Node JS, JavaScript, AWS, Alexa SDK"]}
        client="Lloyds Banking Group"
      />
      <VisualShowcase
        imageUrl={images.lloydsEchoImageTwo}
        imageAlt="Lloyds Banking App"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "Exploring trust, clarity, and security without a screen",
          "As voice assistants became part of everyday life, Lloyds faced a fundamental question: how can customers safely interact with sensitive financial information when there’s no visual interface?",
          "Voice promised speed and convenience, but banking introduced critical challenges around trust, security, and confidence. The task was not to launch a new channel, but to understand whether voice could meaningfully support customers without compromising reassurance.",
        ]}
        solutionContent={[
          "A research-led Alexa prototype designed to learn, not launch",
          "Money Talks was an experimental Alexa experience that enabled customers to make simple, voice-based enquiries about their bank accounts.",
          "I helped shape the experience by defining clear boundaries for voice interaction, focusing on high-confidence tasks rather than feature breadth. By intentionally limiting functionality, the team could study how people naturally talk about money, how trust is built through tone and structure, and where voice genuinely adds value.",
        ]}
        metrics={[
          { value: "90%+", label: "Successful voice enquiries" },
          { value: "Reduced", label: "Cognitive load in routine tasks" },
          { value: "Mapped", label: "Customer financial language patterns" },
          { value: "Validated", label: "Trust without visual UI" },
        ]}
      />
      <VideoEmbed
        src={LloydsVideo}
        poster={images.echoDevice}
        title="Money Talks — Testing Voice Banking Live"
        description="This video captures me designing, coding, and testing the Money Talks prototype on Amazon Echo, using voice to check balances and explore transactions. It’s a live demonstration of how I shaped conversational interactions to ensure clarity, trust, and confidence in a screenless banking experience."
        controls={true}
      />

      <VisualShowcase
        title="Everyday Voice Banking"
        description="A customer checks her balance while making coffee, showing how Money Talks fits seamlessly into daily life. The experiment explored how voice interactions can deliver quick, low-risk banking tasks, build trust without screens, and support natural, human-centered interactions. This moment captures the ease, confidence, and clarity that come from designing for real-life contexts, not just interfaces."
        imageUrl={images.echoDevice3}
        imageAlt="Lloyds Banking App"
        layout="full"
        backgroundColor="black"
        descriptionColor="#9ca3af"
        titleColor="#ffffff"
      />
      <KeyFeatures
        title="Voice Banking in Action"
        description="Features designed for clarity, confidence, and everyday use."
        features={[
          {
            icon: Mic,
            title: "Voice-based balance checks",
            description:
              "Quickly access your account balance using simple voice commands, without needing to open an app or screen.",
          },
          {
            icon: FileText,
            title: "Recent transaction summaries",
            description:
              "Get an overview of recent transactions, helping customers stay informed at a glance.",
          },
          {
            icon: PieChart,
            title: "High-level account information only",
            description:
              "Provides essential account insights without overwhelming users with sensitive details.",
          },
          {
            icon: Lock,
            title: "Secure authentication flows",
            description:
              "All interactions are protected by secure verification to ensure privacy and trust.",
          },
          {
            icon: Shield,
            title: "Clear, reassuring fallback responses",
            description:
              "Guides users calmly when a request can’t be fulfilled, maintaining confidence and clarity.",
          },
          {
            icon: MessageSquare,
            title: "Conversational language aligned to Lloyds’ brand values",
            description:
              "Uses natural, friendly dialogue that reflects Lloyds’ trusted and approachable tone.",
          },
        ]}
      />
      <ProcessSection
        title="Research-Driven Design Process"
        description="A human-centered approach to exploring voice banking, combining prototyping, testing, and cross-functional collaboration to build trust and clarity."
        phases={[
          {
            number: "01",
            title: "Discover & Research",
            description:
              "Mapped natural language patterns and explored how customers phrase financial queries, identifying opportunities and constraints for voice interactions.",
            deliverables: [
              "Customer interviews",
              "Voice interaction mapping",
              "Competitor and market review",
              "Behavioral insights report",
            ],
          },
          {
            number: "02",
            title: "Security & Compliance Alignment",
            description:
              "Worked closely with technology, security, and compliance teams to define authentication flows and ensure sensitive financial data could be handled safely via voice.",
            deliverables: [
              "Secure authentication flows",
              "Compliance review",
              "Privacy guidelines for spoken responses",
              "Risk assessment documentation",
            ],
          },
          {
            number: "03",
            title: "Design & Prototyping",
            description:
              "Crafted concise, low-cognitive-load spoken responses and tested tone, pacing, and phrasing to make interactions natural, clear, and reassuring.",
            deliverables: [
              "Voice prompts and scripts",
              "Interactive Alexa prototype",
              "Design patterns for voice clarity",
              "Iteration log with cross-functional feedback",
            ],
          },
          {
            number: "04",
            title: "User Testing & Validation",
            description:
              "Validated trust signals, comprehension, and usability with real users, ensuring every interaction prioritized clarity, confidence, and restraint.",
            deliverables: [
              "Usability testing sessions",
              "Trust signal validation",
              "Iteration based on feedback",
              "Insights report to inform future voice banking designs",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactDescription="The Money Talks provided Lloyds with practical insight into how customers interact with banking through voice. The project clarified which financial tasks feel appropriate for voice, how trust is built without visual cues, and where conversational interfaces add convenience — or friction. These learnings helped shape future exploration of voice and AI within the organisation, establishing a design-led, customer-first approach to emerging technology experimentation."
        impactLabel="Project Impact"
        impactTitle="Insight-led experimentation with real organisational value"
        nextProject={{
          title: "Thuisbezorgd Loyalty Points Platform",
          link: "/project/thuisbezorgd",
        }}
        conclusion={{
          label: "Design Reflection",
          title: "Responsible experimentation builds real insight",
          description:
            "Designing for emerging technology isn’t about predicting the future — it’s about learning responsibly. Money Talks showed how voice can support banking when interactions are focused, transparent, and grounded in real customer behaviour.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export default Lloyds;
